import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pc_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

axios.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const api = {
  // auth
  register: (username, password, email, beta_code) => axios.post(`${API}/auth/register`, { username, password, email, beta_code }).then((r) => r.data),
  login: (username, password) => axios.post(`${API}/auth/login`, { username, password }).then((r) => r.data),
  betaStatus: () => axios.get(`${API}/auth/beta-status`).then((r) => r.data),
  forgot: (email, origin) => axios.post(`${API}/auth/forgot`, { email, origin }).then((r) => r.data),
  resetPassword: (token, password) => axios.post(`${API}/auth/reset`, { token, password }).then((r) => r.data),
  me: () => axios.get(`${API}/auth/me`).then((r) => r.data),
  // admin
  adminUsers: () => axios.get(`${API}/admin/users`).then((r) => r.data),
  adminResetPassword: (id, password) => axios.post(`${API}/admin/users/${id}/password`, { password }).then((r) => r.data),
  adminSetRole: (id, role) => axios.post(`${API}/admin/users/${id}/role`, { role }).then((r) => r.data),
  adminDeleteUser: (id) => axios.delete(`${API}/admin/users/${id}`).then((r) => r.data),
  adminGetBeta: () => axios.get(`${API}/admin/beta`).then((r) => r.data),
  adminToggleBeta: (enabled) => axios.post(`${API}/admin/beta/toggle`, { enabled }).then((r) => r.data),
  adminCreateBetaCode: (code, note) => axios.post(`${API}/admin/beta/codes`, { code, note }).then((r) => r.data),
  adminUpdateBetaCode: (id, active) => axios.patch(`${API}/admin/beta/codes/${id}`, { active }).then((r) => r.data),
  adminDeleteBetaCode: (id) => axios.delete(`${API}/admin/beta/codes/${id}`).then((r) => r.data),
  // teams (shared collaborative workspaces)
  teams: () => axios.get(`${API}/teams`).then((r) => r.data),
  teamDirectory: () => axios.get(`${API}/teams/directory`).then((r) => r.data),
  createTeam: (name, event_name) => axios.post(`${API}/teams`, { name, event_name }).then((r) => r.data),
  claimTeam: (payload) => axios.post(`${API}/teams/claim`, payload).then((r) => r.data),
  applyTeam: (tid) => axios.post(`${API}/teams/${tid}/apply`).then((r) => r.data),
  joinByCode: (code) => axios.post(`${API}/teams/join`, { code }).then((r) => r.data),
  regenerateCode: (tid) => axios.post(`${API}/teams/${tid}/regenerate-code`).then((r) => r.data),
  approveMember: (tid, user_id) => axios.post(`${API}/teams/${tid}/approve`, { user_id }).then((r) => r.data),
  rejectMember: (tid, user_id) => axios.post(`${API}/teams/${tid}/reject`, { user_id }).then((r) => r.data),
  addMember: (tid, username) => axios.post(`${API}/teams/${tid}/add`, { username }).then((r) => r.data),
  removeMember: (tid, user_id) => axios.post(`${API}/teams/${tid}/remove`, { user_id }).then((r) => r.data),
  setCaptain: (tid, user_id) => axios.post(`${API}/teams/${tid}/captain`, { user_id }).then((r) => r.data),
  deleteTeam: (tid) => axios.delete(`${API}/teams/${tid}`).then((r) => r.data),
  teamSetup: (tid) => axios.get(`${API}/teams/${tid}/setup`).then((r) => r.data),
  teamPatch: (tid, ops) => axios.patch(`${API}/teams/${tid}/setup`, { ops }).then((r) => r.data),
  teamPut: (tid, s) => axios.put(`${API}/teams/${tid}/setup`, s).then((r) => r.data),
  teamSample: (tid) => axios.post(`${API}/teams/${tid}/sample`).then((r) => r.data),
  teamReset: (tid) => axios.post(`${API}/teams/${tid}/reset`).then((r) => r.data),
  teamAnalyze: (tid, state, you_are) => axios.post(`${API}/teams/${tid}/analyze`, { state, you_are }).then((r) => r.data),
  teamHistory: (tid) => axios.get(`${API}/teams/${tid}/history`).then((r) => r.data),
  teamSaveHistory: (tid, e) => axios.post(`${API}/teams/${tid}/history`, e).then((r) => r.data),
  teamDelHistory: (tid, hid) => axios.delete(`${API}/teams/${tid}/history/${hid}`).then((r) => r.data),
  teamUpdateHistory: (tid, hid, p) => axios.patch(`${API}/teams/${tid}/history/${hid}`, p).then((r) => r.data),
  // setup (legacy per-account, kept for migration)
  getSetup: () => axios.get(`${API}/setup`).then((r) => r.data),
  putSetup: (s) => axios.put(`${API}/setup`, s).then((r) => r.data),
  sample: () => axios.post(`${API}/setup/sample`).then((r) => r.data),
  reset: () => axios.post(`${API}/setup/reset`).then((r) => r.data),
  analyze: (state, you_are) => axios.post(`${API}/pairing/analyze`, { state, you_are }).then((r) => r.data),
  // shared imports
  longshanks: (payload) => axios.post(`${API}/longshanks/teams`, payload).then((r) => r.data),
  fetchList: (player_id, event_id) => axios.post(`${API}/longshanks/list`, { player_id, event_id }).then((r) => r.data),
  scraperCredits: () => axios.get(`${API}/scraper/credits`).then((r) => r.data),
  events: () => axios.get(`${API}/events`).then((r) => r.data),
  getEvent: (id) => axios.get(`${API}/events/${id}`).then((r) => r.data),
  saveEvent: (e) => axios.post(`${API}/events`, e).then((r) => r.data),
  delEvent: (id) => axios.delete(`${API}/events/${id}`).then((r) => r.data),
  fetchEventLists: (id, team) => axios.post(`${API}/events/${id}/fetch-lists${team ? `?team=${encodeURIComponent(team)}` : ""}`).then((r) => r.data),
  presets: () => axios.get(`${API}/presets`).then((r) => r.data),
  savePreset: (p) => axios.post(`${API}/presets`, p).then((r) => r.data),
  delPreset: (id) => axios.delete(`${API}/presets/${id}`).then((r) => r.data),
  // history (per account)
  history: () => axios.get(`${API}/history`).then((r) => r.data),
  saveHistory: (e) => axios.post(`${API}/history`, e).then((r) => r.data),
  updateHistory: (id, p) => axios.patch(`${API}/history/${id}`, p).then((r) => r.data),
  delHistory: (id) => axios.delete(`${API}/history/${id}`).then((r) => r.data),
  scouts: () => axios.get(`${API}/scouts`).then((r) => r.data),
  saveScout: (s) => axios.post(`${API}/scouts`, s).then((r) => r.data),
  delScout: (id) => axios.delete(`${API}/scouts/${id}`).then((r) => r.data),
};

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}
